import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Truck, Plus, FileText, Clock, CheckCircle2, XCircle, DollarSign, MapPin, Calendar, LogOut, Menu, X, Package, Star, MessageCircle, CreditCard, ScrollText, Eye, Navigation, Phone, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { TooltipTour, HelpButton, customerTourSteps, customerTutorials } from '../components/TutorialSystem';
import PaymentSetup from '../components/PaymentSetup';
import AIAssistant from '../components/AIAssistant';

export default function CustomerDashboard() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('jobs');
  const [stats, setStats] = useState({});
  const [jobs, setJobs] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNewJobDialog, setShowNewJobDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [tourComplete, setTourComplete] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: '' });
  const [activityLogs, setActivityLogs] = useState([]);
  const [showLogDetailDialog, setShowLogDetailDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [activeTrackingJobs, setActiveTrackingJobs] = useState([]);
  const [selectedTrackingJob, setSelectedTrackingJob] = useState(null);
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const [newJob, setNewJob] = useState({
    title: '',
    description: '',
    job_type: 'household',
    pickup_address: '',
    pickup_city: '',
    pickup_state: '',
    pickup_zip: '',
    delivery_address: '',
    delivery_city: '',
    delivery_state: '',
    delivery_zip: '',
    pickup_date: '',
    weight_estimate: '',
    special_instructions: '',
  });
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    loadDashboard();
    // Get user's location for proximity filtering
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
          // Save to backend
          try {
            await apiClient.patch('/users/location', { latitude, longitude, service_radius: 100 });
          } catch (e) {
            console.log('Could not save location');
          }
        },
        (error) => console.log('Location not available:', error),
        { enableHighAccuracy: false, timeout: 10000 }
      );
    }
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, jobsRes, pendingReviewsRes, logsRes, trackingRes, notifRes] = await Promise.all([
        apiClient.get('/stats/dashboard'),
        apiClient.get('/jobs'),
        apiClient.get('/reviews/pending').catch(() => ({ data: [] })),
        apiClient.get('/logs/my-activity?limit=20').catch(() => ({ data: { logs: [] } })),
        apiClient.get('/tracking/my-active-jobs').catch(() => ({ data: [] })),
        apiClient.get('/notifications?limit=10').catch(() => ({ data: { notifications: [], unread_count: 0 } })),
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setPendingReviews(pendingReviewsRes.data);
      setActivityLogs(logsRes.data.logs || []);
      setActiveTrackingJobs(trackingRes.data || []);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(notifRes.data.unread_count || 0);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadQuotesForJob = async (jobId) => {
    try {
      const res = await apiClient.get(`/quotes/job/${jobId}`);
      setQuotes(prev => ({ ...prev, [jobId]: res.data }));
    } catch (error) {
      toast.error('Failed to load quotes');
    }
  };

  const viewTracking = async (jobId) => {
    try {
      const res = await apiClient.get(`/tracking/job/${jobId}`);
      setSelectedTrackingJob(res.data);
      setShowTrackingDialog(true);
    } catch (error) {
      toast.error('Failed to load tracking data');
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await apiClient.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification read');
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await apiClient.patch('/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications read');
    }
  };

  const viewLogDetail = (log) => {
    setSelectedLog(log);
    setShowLogDetailDialog(true);
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try {
      const jobData = {
        ...newJob,
        pickup_date: new Date(newJob.pickup_date).toISOString(),
        weight_estimate: newJob.weight_estimate ? parseFloat(newJob.weight_estimate) : null,
      };
      await apiClient.post('/jobs', jobData);
      toast.success('Job posted successfully!');
      setShowNewJobDialog(false);
      setNewJob({
        title: '', description: '', job_type: 'household', pickup_address: '', pickup_city: '',
        pickup_state: '', pickup_zip: '', delivery_address: '', delivery_city: '',
        delivery_state: '', delivery_zip: '', pickup_date: '', weight_estimate: '', special_instructions: '',
      });
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    }
  };

  const handleAcceptQuote = async (quoteId, jobId) => {
    try {
      await apiClient.post(`/quotes/${quoteId}/accept`);
      toast.success('Quote accepted! The carrier will contact you soon.');
      loadDashboard();
      loadQuotesForJob(jobId);
    } catch (error) {
      toast.error('Failed to accept quote');
    }
  };

  const handleStartChat = async (carrierId) => {
    try {
      await apiClient.post('/chat/start', { participant_id: carrierId });
      toast.success('Chat started! Click the chat icon to message.');
    } catch (error) {
      toast.error('Failed to start chat');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewTarget) return;
    
    try {
      await apiClient.post('/reviews', {
        carrier_id: reviewTarget.target_id,
        job_id: reviewTarget.job_id,
        rating: reviewData.rating,
        comment: reviewData.comment,
      });
      toast.success('Review submitted successfully!');
      setShowReviewDialog(false);
      setReviewData({ rating: 5, comment: '' });
      setReviewTarget(null);
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const openReviewDialog = (review) => {
    setReviewTarget(review);
    setShowReviewDialog(true);
  };

  const handleSubscribe = async () => {
    try {
      const res = await apiClient.post('/subscriptions/create-checkout');
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start payment process');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge badge-pending',
      quoted: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'badge badge-completed',
      cancelled: 'badge badge-cancelled',
    };
    return badges[status] || 'badge';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Show subscription prompt if not active
  if (user?.subscription_status !== 'active') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Complete Your Setup</h2>
            <p className="text-slate-600 mt-2">
              Add a payment method to start posting jobs. Free to join - you only pay $5 when a job is accepted.
            </p>
          </div>
          <PaymentSetup 
            onSuccess={() => {
              updateUser({ ...user, subscription_status: 'active' });
              navigate('/subscription/success');
            }}
            onCancel={handleLogout}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6 text-orange-500" />
          <span className="font-bold text-slate-900">True Choice Enterprise</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-sidebar-btn">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`sidebar transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform`}>
        <div className="p-6">
          <div className="flex items-center gap-2 text-white mb-8">
            <Truck className="h-8 w-8 text-orange-500" />
            <span className="text-xl font-bold">True Choice</span>
          </div>
          
          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab('jobs')}
              className={`sidebar-link w-full ${activeTab === 'jobs' ? 'sidebar-link-active' : ''}`}
              data-testid="nav-jobs"
            >
              <Package className="h-5 w-5" />
              My Jobs
            </button>
            <button
              onClick={() => setActiveTab('tracking')}
              className={`sidebar-link w-full ${activeTab === 'tracking' ? 'sidebar-link-active' : ''}`}
              data-testid="nav-tracking"
            >
              <Navigation className="h-5 w-5" />
              Track Shipments
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`sidebar-link w-full ${activeTab === 'activity' ? 'sidebar-link-active' : ''}`}
              data-testid="nav-activity"
            >
              <ScrollText className="h-5 w-5" />
              Activity Log
            </button>
            <Link to="/carriers" className="sidebar-link">
              <Star className="h-5 w-5" />
              Find Carriers
            </Link>
          </nav>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-800">
          <div className="text-slate-400 text-sm mb-2">{user?.email}</div>
          <button onClick={handleLogout} className="sidebar-link w-full text-red-400 hover:text-red-300" data-testid="logout-btn">
            <LogOut className="h-5 w-5" />
            Log Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Welcome, {user?.first_name}!</h1>
              <p className="text-slate-600">Manage your moving jobs and quotes</p>
            </div>
            <Dialog open={showNewJobDialog} onOpenChange={setShowNewJobDialog}>
              <DialogTrigger asChild>
                <Button className="btn-accent mt-4 sm:mt-0" data-testid="new-job-btn">
                  <Plus className="h-4 w-4 mr-2" /> Post New Job
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Post a Moving Job</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateJob} className="space-y-4 mt-4">
                  <div>
                    <Label>Job Title</Label>
                    <Input
                      value={newJob.title}
                      onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                      placeholder="e.g., 2 Bedroom Apartment Move"
                      required
                      data-testid="job-title-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Job Type</Label>
                    <Select value={newJob.job_type} onValueChange={(v) => setNewJob({ ...newJob, job_type: v })}>
                      <SelectTrigger data-testid="job-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="household">Household Move</SelectItem>
                        <SelectItem value="freight">Freight</SelectItem>
                        <SelectItem value="cargo">Cargo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={newJob.description}
                      onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
                      placeholder="Describe what needs to be moved..."
                      rows={3}
                      required
                      data-testid="job-description-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-900">Pickup Location</h4>
                      <Input
                        value={newJob.pickup_address}
                        onChange={(e) => setNewJob({ ...newJob, pickup_address: e.target.value })}
                        placeholder="Street Address"
                        required
                        data-testid="pickup-address-input"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          value={newJob.pickup_city}
                          onChange={(e) => setNewJob({ ...newJob, pickup_city: e.target.value })}
                          placeholder="City"
                          required
                        />
                        <Input
                          value={newJob.pickup_state}
                          onChange={(e) => setNewJob({ ...newJob, pickup_state: e.target.value })}
                          placeholder="State"
                          maxLength={2}
                          required
                        />
                        <Input
                          value={newJob.pickup_zip}
                          onChange={(e) => setNewJob({ ...newJob, pickup_zip: e.target.value })}
                          placeholder="ZIP"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-900">Delivery Location</h4>
                      <Input
                        value={newJob.delivery_address}
                        onChange={(e) => setNewJob({ ...newJob, delivery_address: e.target.value })}
                        placeholder="Street Address"
                        required
                        data-testid="delivery-address-input"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          value={newJob.delivery_city}
                          onChange={(e) => setNewJob({ ...newJob, delivery_city: e.target.value })}
                          placeholder="City"
                          required
                        />
                        <Input
                          value={newJob.delivery_state}
                          onChange={(e) => setNewJob({ ...newJob, delivery_state: e.target.value })}
                          placeholder="State"
                          maxLength={2}
                          required
                        />
                        <Input
                          value={newJob.delivery_zip}
                          onChange={(e) => setNewJob({ ...newJob, delivery_zip: e.target.value })}
                          placeholder="ZIP"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Pickup Date</Label>
                      <Input
                        type="datetime-local"
                        value={newJob.pickup_date}
                        onChange={(e) => setNewJob({ ...newJob, pickup_date: e.target.value })}
                        required
                        data-testid="pickup-date-input"
                      />
                    </div>
                    <div>
                      <Label>Estimated Weight (lbs)</Label>
                      <Input
                        type="number"
                        value={newJob.weight_estimate}
                        onChange={(e) => setNewJob({ ...newJob, weight_estimate: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Special Instructions</Label>
                    <Textarea
                      value={newJob.special_instructions}
                      onChange={(e) => setNewJob({ ...newJob, special_instructions: e.target.value })}
                      placeholder="Any special requirements..."
                      rows={2}
                    />
                  </div>
                  
                  <Button type="submit" className="btn-accent w-full" data-testid="submit-job-btn">
                    Post Job
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="stat-card">
              <div className="stat-value">{stats.total_jobs || 0}</div>
              <div className="stat-label">Total Jobs</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.active_jobs || 0}</div>
              <div className="stat-label">Active Jobs</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.completed_jobs || 0}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_quotes || 0}</div>
              <div className="stat-label">Quotes Received</div>
            </div>
          </div>

          {/* Pending Reviews Section */}
          {pendingReviews.length > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6 mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Star className="h-6 w-6 text-amber-500" />
                <h2 className="text-lg font-bold text-slate-900">Rate Your Recent Jobs</h2>
              </div>
              <p className="text-slate-600 mb-4">Help other customers by sharing your experience with these carriers.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingReviews.map((review) => (
                  <div key={review.job_id} className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                    <h3 className="font-semibold text-slate-900">{review.job_title}</h3>
                    <p className="text-sm text-slate-500 mt-1">Carrier: {review.target_name}</p>
                    <Button
                      onClick={() => openReviewDialog(review)}
                      className="btn-accent mt-3 w-full"
                      data-testid={`review-btn-${review.job_id}`}
                    >
                      <Star className="h-4 w-4 mr-2" /> Leave Review
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Jobs List Tab */}
          {activeTab === 'jobs' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">My Jobs</h2>
              </div>
              
              {jobs.length === 0 ? (
                <div className="empty-state py-16">
                  <FileText className="empty-state-icon" />
                  <p className="empty-state-text">No jobs posted yet</p>
                  <Button onClick={() => setShowNewJobDialog(true)} className="btn-primary mt-4">
                    Post Your First Job
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {jobs.map((job) => (
                    <div key={job.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-slate-900">{job.title}</h3>
                            <span className={`badge ${getStatusBadge(job.status)}`}>
                              {job.status.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">{job.description}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {job.pickup_city}, {job.pickup_state} → {job.delivery_city}, {job.delivery_state}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {format(new Date(job.pickup_date), 'MMM d, yyyy')}
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="h-4 w-4" />
                              {job.quote_count} quotes
                            </span>
                          </div>
                        </div>
                        
                        {job.quote_count > 0 && (
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSelectedJob(selectedJob === job.id ? null : job.id);
                              if (selectedJob !== job.id) loadQuotesForJob(job.id);
                            }}
                            data-testid={`view-quotes-${job.id}`}
                          >
                            {selectedJob === job.id ? 'Hide Quotes' : 'View Quotes'}
                          </Button>
                        )}
                      </div>
                      
                      {/* Quotes section */}
                      {selectedJob === job.id && quotes[job.id] && (
                        <div className="mt-6 bg-slate-50 rounded-lg p-4">
                          <h4 className="font-semibold text-slate-900 mb-4">Quotes Received</h4>
                          <div className="space-y-4">
                            {quotes[job.id].map((quote) => (
                              <div key={quote.id} className="bg-white rounded-lg p-4 border border-slate-200">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                  <div>
                                    <div className="font-semibold text-slate-900">
                                      {quote.carrier_company || quote.carrier_name}
                                    </div>
                                    <div className="text-2xl font-bold text-orange-500 mt-1">
                                      ${quote.amount.toFixed(2)}
                                    </div>
                                    <div className="text-sm text-slate-500 mt-1">
                                      Est. delivery: {format(new Date(quote.estimated_delivery), 'MMM d, yyyy')}
                                    </div>
                                    {quote.notes && (
                                      <p className="text-sm text-slate-600 mt-2">{quote.notes}</p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="outline"
                                      onClick={() => handleStartChat(quote.carrier_id)}
                                      data-testid={`chat-carrier-${quote.carrier_id}`}
                                    >
                                      <MessageCircle className="h-4 w-4 mr-2" /> Chat
                                    </Button>
                                    {quote.status === 'pending' && (
                                      <Button
                                        onClick={() => handleAcceptQuote(quote.id, job.id)}
                                        className="btn-accent"
                                        data-testid={`accept-quote-${quote.id}`}
                                      >
                                        <CheckCircle2 className="h-4 w-4 mr-2" /> Accept
                                      </Button>
                                    )}
                                    {quote.status === 'accepted' && (
                                      <span className="badge badge-active">Accepted</span>
                                    )}
                                    {quote.status === 'rejected' && (
                                      <span className="badge badge-cancelled">Rejected</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Activity Logs Tab */}
          {activeTab === 'activity' && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="activity-log-section">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">Activity Log</h2>
                <p className="text-slate-500 mt-1">Your job history with BOL and contract details</p>
              </div>
              
              {activityLogs.length === 0 ? (
                <div className="p-12 text-center">
                  <ScrollText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No activity logged yet</p>
                  <p className="text-sm text-slate-400 mt-1">Your accepted jobs will appear here</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {activityLogs.map((log) => (
                    <div key={log.id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.log_type === 'quote_accepted' ? 'bg-green-100 text-green-700' :
                              log.log_type === 'job_completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {log.log_type.replace('_', ' ').toUpperCase()}
                            </span>
                            <span className="text-sm text-slate-500">
                              {format(new Date(log.created_at), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                          <h3 className="font-semibold text-slate-900">{log.job_title}</h3>
                          <p className="text-slate-600 text-sm mt-1">{log.description}</p>
                          
                          {log.bol_number && (
                            <div className="mt-3 flex items-center gap-4 text-sm">
                              <span className="text-slate-500">
                                <FileText className="h-4 w-4 inline mr-1" />
                                BOL: <span className="font-mono font-medium text-slate-700">{log.bol_number}</span>
                              </span>
                              {log.amount && (
                                <span className="text-slate-500">
                                  <DollarSign className="h-4 w-4 inline mr-1" />
                                  Amount: <span className="font-medium text-green-600">${log.amount.toFixed(2)}</span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewLogDetail(log)}
                          data-testid={`view-log-${log.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" /> View Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tracking Tab */}
          {activeTab === 'tracking' && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="tracking-section">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">Track Your Shipments</h2>
                <p className="text-slate-500 mt-1">Real-time location tracking for your active jobs</p>
              </div>
              
              {activeTrackingJobs.length === 0 ? (
                <div className="p-12 text-center">
                  <Navigation className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No shipments to track</p>
                  <p className="text-sm text-slate-400 mt-1">Accept a quote to start tracking your shipment</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {activeTrackingJobs.map((job) => (
                    <div key={job.job_id} className="p-6 hover:bg-slate-50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">{job.job_title}</h3>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              job.tracking_status === 'delivered' ? 'bg-green-100 text-green-700' :
                              job.tracking_status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                              job.tracking_status === 'picked_up' ? 'bg-amber-100 text-amber-700' :
                              job.tracking_status === 'near_delivery' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {job.tracking_status.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          
                          <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-slate-500">From:</span>
                              <p className="font-medium text-slate-700">{job.pickup_city}, {job.pickup_state}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">To:</span>
                              <p className="font-medium text-slate-700">{job.delivery_city}, {job.delivery_state}</p>
                            </div>
                          </div>
                          
                          {job.carrier_name && (
                            <p className="text-sm text-slate-600 mt-2">
                              <Truck className="h-4 w-4 inline mr-1" />
                              Carrier: <span className="font-medium">{job.carrier_name}</span>
                            </p>
                          )}
                          
                          {job.last_updated && (
                            <p className="text-xs text-slate-400 mt-2">
                              Last updated: {format(new Date(job.last_updated), 'MMM d, h:mm a')}
                            </p>
                          )}
                        </div>
                        
                        <Button
                          onClick={() => viewTracking(job.job_id)}
                          className="btn-accent"
                          data-testid={`track-job-${job.job_id}`}
                        >
                          <Navigation className="h-4 w-4 mr-2" /> View Tracking
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Tooltip Tour for first-time users */}
      {!loading && (
        <TooltipTour 
          steps={customerTourSteps} 
          storageKey="customer_tour_complete"
          onComplete={() => setTourComplete(true)}
        />
      )}

      {/* Help Button with Video Tutorials */}
      <HelpButton tutorials={customerTutorials} />

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Rate Your Experience
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitReview} className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-700">Job: {reviewTarget?.job_title}</Label>
              <p className="text-sm text-slate-500">Carrier: {reviewTarget?.target_name}</p>
            </div>
            
            <div>
              <Label>Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewData({ ...reviewData, rating: star })}
                    className={`p-2 rounded-lg transition-all ${
                      star <= reviewData.rating 
                        ? 'text-amber-400 bg-amber-50 hover:bg-amber-100' 
                        : 'text-slate-300 bg-slate-50 hover:bg-slate-100'
                    }`}
                    data-testid={`star-${star}`}
                  >
                    <Star className={`h-8 w-8 ${star <= reviewData.rating ? 'fill-amber-400' : ''}`} />
                  </button>
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {reviewData.rating === 1 && 'Poor'}
                {reviewData.rating === 2 && 'Fair'}
                {reviewData.rating === 3 && 'Good'}
                {reviewData.rating === 4 && 'Very Good'}
                {reviewData.rating === 5 && 'Excellent'}
              </p>
            </div>
            
            <div>
              <Label>Comment (Optional)</Label>
              <Textarea
                value={reviewData.comment}
                onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
                placeholder="Share your experience with this carrier..."
                rows={4}
                data-testid="review-comment"
              />
            </div>
            
            <Button type="submit" className="btn-accent w-full" data-testid="submit-review-btn">
              Submit Review
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Log Detail Dialog */}
      <Dialog open={showLogDetailDialog} onOpenChange={setShowLogDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText className="h-5 w-5 text-slate-500" />
              Activity Details
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600">Job Title</Label>
                  <p className="font-medium text-slate-900">{selectedLog.job_title}</p>
                </div>
                <div>
                  <Label className="text-slate-600">Activity Type</Label>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                    selectedLog.log_type === 'quote_accepted' ? 'bg-green-100 text-green-700' :
                    selectedLog.log_type === 'job_completed' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {selectedLog.log_type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-600">Description</Label>
                <p className="text-slate-900">{selectedLog.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600">Date & Time</Label>
                  <p className="text-slate-900">{format(new Date(selectedLog.created_at), 'MMM d, yyyy h:mm a')}</p>
                </div>
                {selectedLog.amount && (
                  <div>
                    <Label className="text-slate-600">Amount</Label>
                    <p className="text-green-600 font-medium">${selectedLog.amount.toFixed(2)}</p>
                  </div>
                )}
              </div>
              
              {selectedLog.bol_number && (
                <div>
                  <Label className="text-slate-600">BOL Number</Label>
                  <p className="font-mono text-slate-900 bg-slate-50 px-3 py-2 rounded border">
                    {selectedLog.bol_number}
                  </p>
                </div>
              )}
              
              {selectedLog.metadata && (
                <div>
                  <Label className="text-slate-600">Additional Details</Label>
                  <div className="bg-slate-50 rounded-lg p-4 mt-2">
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tracking Detail Dialog */}
      <Dialog open={showTrackingDialog} onOpenChange={setShowTrackingDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="h-5 w-5 text-blue-500" />
              Shipment Tracking
            </DialogTitle>
          </DialogHeader>
          {selectedTrackingJob && (
            <div className="mt-4 space-y-6">
              {/* Job Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900">{selectedTrackingJob.job_title}</h4>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedTrackingJob.tracking_status === 'delivered' ? 'bg-green-100 text-green-700' :
                    selectedTrackingJob.tracking_status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                    selectedTrackingJob.tracking_status === 'picked_up' ? 'bg-amber-100 text-amber-700' :
                    selectedTrackingJob.tracking_status === 'near_delivery' ? 'bg-purple-100 text-purple-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {selectedTrackingJob.tracking_status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>
              
              {/* Carrier Info */}
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <h4 className="font-semibold text-orange-900 mb-2">Carrier Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-orange-700">Company</span>
                    <p className="font-semibold text-orange-900">{selectedTrackingJob.carrier_name}</p>
                  </div>
                  {selectedTrackingJob.carrier_phone && (
                    <div>
                      <span className="text-orange-700">Phone</span>
                      <p className="font-semibold text-orange-900 flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <a href={`tel:${selectedTrackingJob.carrier_phone}`} className="underline">
                          {selectedTrackingJob.carrier_phone}
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Route Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Pickup
                  </h4>
                  <p className="text-sm text-green-700">{selectedTrackingJob.pickup_location?.address}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Delivery
                  </h4>
                  <p className="text-sm text-blue-700">{selectedTrackingJob.delivery_location?.address}</p>
                </div>
              </div>
              
              {/* Current Location */}
              {selectedTrackingJob.current_location && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h4 className="font-semibold text-purple-900 mb-2">Current Location</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-purple-700">Coordinates</span>
                      <p className="font-mono text-purple-900">
                        {selectedTrackingJob.current_location.latitude?.toFixed(4)}, 
                        {selectedTrackingJob.current_location.longitude?.toFixed(4)}
                      </p>
                    </div>
                    <div>
                      <span className="text-purple-700">Last Update</span>
                      <p className="text-purple-900">
                        {format(new Date(selectedTrackingJob.current_location.timestamp), 'MMM d, h:mm a')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Location History */}
              {selectedTrackingJob.location_history?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Recent Updates</h4>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {selectedTrackingJob.location_history.slice(-5).reverse().map((loc, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm bg-slate-50 rounded p-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <span className="font-mono text-slate-600">
                          {loc.latitude?.toFixed(4)}, {loc.longitude?.toFixed(4)}
                        </span>
                        <span className="text-slate-400 text-xs ml-auto">
                          {format(new Date(loc.timestamp), 'h:mm a')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Assistant */}
      <AIAssistant 
        token={localStorage.getItem('token')} 
        userRole="customer"
        onJobDescriptionImproved={(description) => {
          toast.success('Job description improved! Copy it to your new job.');
        }}
      />
    </div>
  );
}
